import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { RefreshCw, Clock, CheckCircle, XCircle, Calendar, CreditCard } from 'lucide-react';
import { PaymentButton } from '@/components/PaymentButton';

const CURRICULUM_RENEWAL_FEE = 20000;

const statusConfig = {
  pending: { color: 'bg-amber-100 text-amber-700', icon: Clock, label: 'Pending' },
  payment_pending: { color: 'bg-blue-100 text-blue-700', icon: CreditCard, label: 'Payment Pending' },
  approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Approved' },
  rejected: { color: 'bg-red-100 text-red-700', icon: XCircle, label: 'Rejected' }
};

export function CurriculumRenewalsTab({ renewals, onPayment, onShowBankTransfer }) {
  if (!renewals || renewals.length === 0) {
    return (
      <div className="text-center py-12">
        <RefreshCw className="h-12 w-12 mx-auto mb-4 text-slate-300" />
        <p className="text-slate-500">No renewal requests</p>
        <p className="text-xs text-slate-400 mt-2">
          Renewals can be initiated from the Certificates tab when expiry approaches
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {renewals.map((renewal) => {
        const config = statusConfig[renewal.status] || statusConfig.pending;
        const StatusIcon = config.icon;
        const needsPayment = renewal.status === 'payment_pending' || renewal.fee_status === 'pending';
        
        return (
          <Card key={renewal.id}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="font-semibold text-[#1B2A4A]">{renewal.curriculum_title}</h3>
                    <Badge className={config.color}>
                      <StatusIcon className="h-3 w-3 mr-1" />
                      {config.label}
                    </Badge>
                  </div>
                  
                  <p className="text-sm text-slate-600 mb-2">{renewal.institution_name}</p>
                  
                  <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Calendar className="h-3 w-3" />
                      Requested: {new Date(renewal.created_at).toLocaleDateString()}
                    </span>
                    {renewal.previous_expiry && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        Previous Expiry: {new Date(renewal.previous_expiry).toLocaleDateString()}
                      </span>
                    )}
                    {renewal.new_expiry && (
                      <span className="flex items-center gap-1 text-green-600">
                        <Calendar className="h-3 w-3" />
                        New Expiry: {new Date(renewal.new_expiry).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  
                  {renewal.admin_comments && (
                    <div className="mt-3 p-3 bg-slate-50 rounded-sm">
                      <p className="text-xs text-slate-600">{renewal.admin_comments}</p>
                    </div>
                  )}
                </div>
                
                {needsPayment && (
                  <div className="text-right">
                    <p className="text-xs text-slate-500 mb-1">Renewal Fee</p>
                    <p className="text-lg font-bold text-[#1B2A4A] mb-2">
                      ₹{CURRICULUM_RENEWAL_FEE.toLocaleString()}
                    </p>
                    <div className="flex flex-col gap-2">
                      <PaymentButton
                        amount={CURRICULUM_RENEWAL_FEE}
                        description={`Curriculum Renewal - ${renewal.curriculum_title}`}
                        onSuccess={(paymentId) => onPayment(renewal.id, paymentId)}
                        className="bg-green-600 hover:bg-green-700 text-white text-xs rounded-sm"
                      >
                        <CreditCard className="h-3 w-3 mr-1" /> Pay Online
                      </PaymentButton>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs rounded-sm"
                        onClick={() => onShowBankTransfer(renewal)}
                      >
                        Bank Transfer
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}
